import {
  BoltTaskStatuses,
  BoltTaskTypePriorities,
  IBoltTaskSLA,
  IBoltTaskSOP,
  IBoltTaskTemplate,
  INewBoltTaskTemplate,
  IPerson,
  SLAUnits,
  TaskExecutionTypeFieldSchema,
} from "@bolt/core";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import {
  EnumSingleSelectFieldV2,
  EnumSingleSelectFieldV2Props,
  NumericField,
  NumericFieldProps,
  SchemaFactoryV2,
  TextField,
  TextFieldProps,
  ComplexFieldProps,
  RichTextEditorField,
  RichTextEditorFieldProps,
  RichTextEditorValueType,
  utils,
  AsyncODataDropdownField,
  fieldTypes,
} from "@ucl/library";
import { get } from "lodash";
import { SLAComponent } from "../../SlaComponent/SlaComponent";
import { SOPComponent } from "../../SOPComponent/SOPComponent";
import {
  taskPriorityOptions,
  optionalTaskStatus,
} from "./CreateBoltTaskTemplateFormContainer";
import { RichTextEditorsOutputTextTypes } from "../../../types/globalTypes";
import { TaskExecutionTypeFieldsSection } from "../TaskExecutionTemplateFieldsSection";
export const singleStepTaskTypeForm = (
  factory: SchemaFactoryV2,
  apiBaseUrl: string,
  assigneeOdataUrl: string,
  taskType: IBoltTaskTemplate | INewBoltTaskTemplate,
  updateTaskTemplate: (
    taskTemplate: IBoltTaskTemplate | INewBoltTaskTemplate
  ) => void,
  getUserBearerTokenRequestHeader: () => Promise<
    Record<string, string> | undefined
  >,
  richTextEditorsOutputTextType?: RichTextEditorsOutputTextTypes,
  errors?: { [key: string]: string[] },
  taskExecutionTypeOptions?: fieldTypes.Option<string>[],
  taskExecutionTypeData?: TaskExecutionTypeFieldSchema,
  updateTaskExecutionTypeData?: (
    updatedModel: TaskExecutionTypeFieldSchema
  ) => void,
  entityTypeOptions?: fieldTypes.Option<string>[] | [] | undefined
) => {
  const taskTypeSchema = get(factory.schema?.schema, "BoltTaskTemplate");
  const richTextEditorOutputTypeIsPlainText =
    !utils.miscellaneous.isNullOrUndefined(richTextEditorsOutputTextType) &&
    richTextEditorsOutputTextType === RichTextEditorsOutputTextTypes.PLAIN;
  const showOnHolidays = taskType.initialStatus != BoltTaskStatuses.OnHold;

  return (
    <>
      <div className="task-type-form-flex-wrapper">
        <TooltipComponent
          content={`Name of the Task Template.`}
          position="BottomCenter"
          target=".task-type-name .bp5-form-content"
        >
          <div className="task-type-name">
            <TextField
              {...(get(
                taskTypeSchema,
                "BoltTaskTemplate__TaskTemplateName"
              ) as TextFieldProps)}
              errorMessages={get(errors, "boltTaskTemplateName")}
              value={taskType.taskTemplateName}
              onSubmit={(value) => {
                updateTaskTemplate({
                  ...taskType,
                  taskTemplateName: value as string,
                });
              }}
              isRequired
            />
          </div>
        </TooltipComponent>

        <EnumSingleSelectFieldV2
          type="EnumSingleSelect"
          label="Entity"
          optionValues={entityTypeOptions || []}
          value={taskType.entityType}
          onSubmit={(value) => {
            updateTaskTemplate({
              ...taskType,
              entityType: value as string,
              taskExecutionType: undefined,
              taskExecutionTypeName: undefined,
              taskExecutionTypeFields: undefined,
            });
          }}
        />

        {taskType.entityType && (
          <TaskExecutionTypeFieldsSection
            key={taskType.entityType}
            taskTemplate={taskType}
            updateTaskTemplate={updateTaskTemplate}
            taskExecutionTypeOptions={taskExecutionTypeOptions}
            getAuthRequestHeader={getUserBearerTokenRequestHeader}
            taskExecutionTypeData={taskExecutionTypeData}
            updateTaskExecutionTypeData={updateTaskExecutionTypeData}
            factory={factory}
            apiBaseUrl={apiBaseUrl}
            errors={errors}
          />
        )}
      </div>

      <RichTextEditorField
        {...(get(
          taskTypeSchema,
          "BoltTaskTemplate__TaskTemplateDescription"
        ) as RichTextEditorFieldProps)}
        richTextEditorSettings={{
          toolbarSettings: {
            enable: richTextEditorOutputTypeIsPlainText ? false : true,
          },
        }}
        getPlainTextOnSubmit={(value) => {
          if (richTextEditorOutputTypeIsPlainText) {
            updateTaskTemplate({
              ...taskType,
              taskTemplateDescription: value as string,
            });
          }
        }}
        disabled={false}
        placeholder="Type here to add a description..."
        value={taskType.taskTemplateDescription}
        valueType={RichTextEditorValueType.html}
        onSubmit={(value) => {
          if (!richTextEditorOutputTypeIsPlainText) {
            updateTaskTemplate({
              ...taskType,
              taskTemplateDescription: value as string,
            });
          }
        }}
        isRequired
      />

      <div className="task-type-form-flex-wrapper-2">
        <AsyncODataDropdownField
          type="AsyncODataDropdown"
          label="Assignee"
          odataUrl={assigneeOdataUrl}
          getAuthRequestHeader={getUserBearerTokenRequestHeader}
          value={taskType.assigneeId}
          textColumnName={"fullName"}
          valueColumnName={"id"}
          groupByColumnName="type"
          renderProps={{
            showHeader: false,
          }}
          onSubmit={(value) => {
            const state = value as unknown as IPerson;
            const assigneeId = (state as unknown as IPerson)?.id ?? "";

            updateTaskTemplate({
              ...taskType,
              assigneeId,
            });
          }}
        />
        <div className="task-type-form--spacer"></div>
        <SLAComponent
          {...(get(
            taskTypeSchema,
            "BoltTaskTemplate__SLA_AsObject"
          ) as ComplexFieldProps<IBoltTaskSLA>)}
          value={{ sla: taskType.sla, slaUnit: taskType.slaUnit }}
          availableSLAUnits={[SLAUnits.Day, SLAUnits.Hour]}
          onSubmit={function (value: IBoltTaskSLA) {
            updateTaskTemplate({
              ...taskType,
              sla: value.sla,
              slaUnit: value.slaUnit,
            });
          }}
          isRequired
        />
        <div className="task-type-form--spacer"></div>
        <SLAComponent
          {...(get(
            taskTypeSchema,
            "BoltTaskTemplate__ExpectedSLA_AsObject"
          ) as ComplexFieldProps<IBoltTaskSLA>)}
          value={{
            sla: taskType.expectedSLA,
            slaUnit: taskType.expectedSLAUnit,
          }}
          availableSLAUnits={[SLAUnits.Minute]}
          onSubmit={function (value: IBoltTaskSLA) {
            updateTaskTemplate({
              ...taskType,
              expectedSLA: value.sla,
              expectedSLAUnit: SLAUnits.Minute,
            });
          }}
        />
      </div>
      <SOPComponent
        {...(get(
          taskTypeSchema,
          "BoltTaskTemplate__SOP_AsObject"
        ) as ComplexFieldProps<IBoltTaskSOP>)}
        value={{
          sop: taskType.taskTemplateSOP,
          sop_AsList: taskType.taskTemplateSOP_AsList,
        }}
        onSubmit={function (value: IBoltTaskSOP) {
          updateTaskTemplate({
            ...taskType,
            taskTemplateSOP_AsList: value.sop_AsList || [],
          });
        }}
      />
      <div className="task-type-form-flex-wrapper-2">
        <EnumSingleSelectFieldV2
          {...(get(
            taskTypeSchema,
            "BoltTaskTemplate__InitialStatus"
          ) as EnumSingleSelectFieldV2Props)}
          value={taskType.initialStatus}
          optionValues={optionalTaskStatus.filter(
            (o) => o.value != 2 && o.value != 4
          )}
          onSubmit={(value) => {
            updateTaskTemplate({
              ...taskType,
              initialStatus: value as BoltTaskStatuses,
            });
          }}
        />
        <div className="task-type-onholddays" hidden={showOnHolidays}>
          <NumericField
            {...(get(
              taskTypeSchema,
              "BoltTaskTemplate__OnHoldDays"
            ) as NumericFieldProps)}
            format="n0"
            showIncrementButtons={false}
            value={taskType.onHoldDays}
            onSubmit={function (value) {
              updateTaskTemplate({
                ...taskType,
                onHoldDays: value as number,
              });
            }}
          />
        </div>
        <div className={showOnHolidays ? "task-type-priority-field" : ""}>
          <EnumSingleSelectFieldV2
            {...(get(
              taskTypeSchema,
              "BoltTaskTemplate__TaskPriority"
            ) as EnumSingleSelectFieldV2Props)}
            isRequired
            value={taskType.taskPriority}
            optionValues={taskPriorityOptions}
            onSubmit={(value) => {
              updateTaskTemplate({
                ...taskType,
                taskPriority: value as BoltTaskTypePriorities,
              });
            }}
          />
        </div>
      </div>
    </>
  );
};
